/**
 * localStorage and sessionStorage that cannot take the page down.
 *
 * Reading either throws — a SecurityError, not null — when site data is blocked: Safari's "block all
 * cookies", an enterprise policy, a third-party frame with storage partitioned away. This app is opened
 * from a D365 host window, which is exactly that case. A read at module scope that throws stops the whole
 * bundle from evaluating, so nothing renders and no error boundary can catch it. Every read and write goes
 * through here and answers null or does nothing instead.
 */
function get(kind: "local" | "session", key: string): string | null {
  try {
    return (kind === "local" ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function set(kind: "local" | "session", key: string, value: string): void {
  try {
    (kind === "local" ? window.localStorage : window.sessionStorage).setItem(key, value);
  } catch {
    /* storage blocked or full — the value simply does not persist */
  }
}

function remove(kind: "local" | "session", key: string): void {
  try {
    (kind === "local" ? window.localStorage : window.sessionStorage).removeItem(key);
  } catch {
    /* storage blocked — nothing to remove */
  }
}

export const localGet = (key: string) => get("local", key);
export const localSet = (key: string, value: string) => set("local", key, value);
export const localRemove = (key: string) => remove("local", key);
export const sessionGet = (key: string) => get("session", key);
export const sessionSet = (key: string, value: string) => set("session", key, value);
export const sessionRemove = (key: string) => remove("session", key);
